/**
 * Test measurements: one card per circuit (insulation, PE continuity, fault loop in TN) and per
 * RCD (trip time, trip current, test button), with the verdict next to each value. The same
 * editor serves the desktop dialog and the site survey (on the diagrams the survey would give).
 * One measurement per circuit letter / RCD tag, dated with the day of the test.
 */
import { useMemo, useState } from 'react';
import type { Analysis, CircuitInfo } from '@elec/analysis';
import { localeTag, newId, type Element, type Measurement, type Op, type Project } from '@elec/domain';
import type { Locale, Translate } from '@elec/i18n';
import { surveyBoards } from '@elec/domain';
import { earthResistanceOps } from '@elec/survey';
import {
  DEFAULT_TEST_VOLTAGE_V,
  earthNeeds,
  latestMeasurements,
  limitText,
  measurementTargets,
  measurementVerdicts,
  type FieldVerdict,
  type MeasureField,
} from '@elec/rules';
import { Chips, NumberField } from './surveyUi.tsx';
import { Dialog } from './ui.tsx';

const today = () => new Date().toISOString().slice(0, 10);

/** Measured / to measure, for a summary line. */
export function measurementProgress(project: Project, a: Analysis) {
  const { circuits, rcds } = measurementTargets(a);
  const latest = latestMeasurements(project);
  return {
    circuits: circuits.size,
    rcds: rcds.size,
    measured: [...circuits.keys()].filter((k) => latest.circuits.get(k)?.insulationMOhm !== undefined).length,
    tested: [...rcds.keys()].filter((k) => latest.rcds.get(k)?.tripMs !== undefined).length,
    failed: [
      ...[...circuits].map(([k, c]) => {
        const m = latest.circuits.get(k);
        return m ? measurementVerdicts(m, { circuit: c }, project.meta.earthingSystem) : [];
      }),
      ...[...rcds].map(([k, r]) => {
        const m = latest.rcds.get(k);
        return m ? measurementVerdicts(m, { rcd: r }, project.meta.earthingSystem) : [];
      }),
    ].filter((v) => v.some((x) => !x.ok)).length,
  };
}

export function MeasurementsEditor(props: {
  project: Project;
  /** Analysis of the diagrams the measurements refer to (the project's, or the survey preview's). */
  analysis: Analysis;
  t: Translate;
  locale: Locale;
  readOnly?: boolean;
  dispatch(label: string, ops: Op[]): void;
}) {
  const { project, analysis, t, locale } = props;
  const [date, setDate] = useState(today);
  const { circuits, rcds } = useMemo(() => measurementTargets(analysis), [analysis]);
  const latest = useMemo(() => latestMeasurements(project), [project]);
  const tn = project.meta.earthingSystem.startsWith('TN');
  const en = locale === 'en';

  /** Set fields of the measurement of `ref`, creating it if there is none. */
  const save = (target: Measurement['target'], ref: string, patch: Partial<Measurement>) => {
    const old = (target === 'circuit' ? latest.circuits : latest.rcds).get(ref);
    const ops: Op[] = old
      ? [{ op: 'update', coll: 'measurements', id: old.id, patch: { ...patch, date } }]
      : [
          {
            op: 'add',
            coll: 'measurements',
            value: { id: newId(), target, ref, date, notes: '', ...patch } as Measurement,
          },
        ];
    props.dispatch(t('measure.tx', { ref }), ops);
  };

  const verdictOf = (v: FieldVerdict[], f: MeasureField) => v.find((x) => x.field === f);
  /** A failed card: a new measurement after the repair, the failed one kept for the record. */
  const again = (target: Measurement['target'], ref: string, old: Measurement) =>
    props.dispatch(t('measure.again'), [
      {
        op: 'add',
        coll: 'measurements',
        value: { id: newId(), target, ref, date, notes: '', retestOf: old.id } as Measurement,
      },
    ]);
  const FIELDS: Array<[keyof Measurement, string]> = [
    ['insulationMOhm', 'MΩ'],
    ['continuityOhm', 'Ω'],
    ['loopOhm', 'Ω'],
    ['tripMs', 'ms'],
    ['tripMa', 'mA'],
  ];
  /** "Before: 0,3 MΩ (12/10/2026)" under a retest. */
  const previous = (m: Measurement | undefined) => {
    const prev = m?.retestOf ? project.measurements[m.retestOf] : undefined;
    if (!prev) return null;
    const what = FIELDS.filter(([k]) => prev[k] !== undefined)
      .map(([k, u]) => `${String(prev[k]).replace('.', en ? '.' : ',')} ${u}`)
      .join(', ');
    const d = new Date(`${prev.date}T12:00:00`).toLocaleDateString(localeTag(locale));
    return <p className="ms-previous">{t('measure.previous', { what: what || '—', date: d })}</p>;
  };
  const againButton = (
    target: Measurement['target'],
    ref: string,
    m: Measurement | undefined,
    bad: boolean,
  ) =>
    bad && m && !props.readOnly ? (
      <button className="sv-link" onClick={() => again(target, ref, m)} data-testid={`ms-again-${ref}`}>
        {t('measure.again')}
      </button>
    ) : null;
  const hint = (v: FieldVerdict | undefined, fallback: string) =>
    v ? (
      <span className={`ms-verdict ${v.ok ? 'ok' : 'bad'}`} data-verdict={v.ok ? 'ok' : 'fail'}>
        {v.ok ? '✓' : '✗'} {limitText(v, en)}
      </span>
    ) : (
      <span className="ms-verdict">{fallback}</span>
    );

  const circuitCard = (code: string, c: CircuitInfo) => {
    const m = latest.circuits.get(code);
    const v = m ? measurementVerdicts(m, { circuit: c }, project.meta.earthingSystem) : [];
    const bad = v.some((x) => !x.ok);
    const desc = [
      c.circuit?.label,
      c.ratingA ? `${c.device.props.curve ?? ''}${c.ratingA} A` : '',
      c.cable.sectionMm2 ? `${String(c.cable.sectionMm2).replace('.', ',')} mm²` : '',
    ]
      .filter(Boolean)
      .join(' · ');
    const field = (key: 'insulationMOhm' | 'continuityOhm' | 'loopOhm', label: string, fallback: string) => (
      <div className="ms-field">
        <NumberField
          label={label}
          value={m?.[key]}
          onCommit={(x) => save('circuit', code, { [key]: x })}
          testId={`ms-${code}-${key}`}
        />
        {hint(verdictOf(v, key), fallback)}
      </div>
    );
    return (
      <div
        className={`ms-card${bad ? ' bad' : m ? ' done' : ''}`}
        key={`c-${code}`}
        data-testid={`ms-circuit-${code}`}
      >
        <div className="ms-head">
          <strong className="mono">{code}</strong>
          <span>{desc}</span>
          {m && (
            <span className="ms-date">
              {new Date(`${m.date}T12:00:00`).toLocaleDateString(localeTag(locale))}
            </span>
          )}
        </div>
        <fieldset className="ms-fields" disabled={props.readOnly}>
          {field('insulationMOhm', t('measure.insulation'), '')}
          <div className="ms-field">
            <span className="ms-label">{t('measure.testVoltage')}</span>
            <Chips
              value={m?.testVoltageV ?? DEFAULT_TEST_VOLTAGE_V}
              options={[250, 500, 1000].map((x) => ({ value: x, label: `${x} V` }))}
              onChange={(x) => x && save('circuit', code, { testVoltageV: x })}
              label={t('measure.testVoltage')}
            />
          </div>
          {field('continuityOhm', t('measure.continuity'), '')}
          {tn &&
            field('loopOhm', t('measure.loop'), c.device.symbolId === 'iec.fuse' ? t('measure.noLimit') : '')}
        </fieldset>
        {previous(m)}
        {againButton('circuit', code, m, bad)}
      </div>
    );
  };

  const rcdCard = (tag: string, r: Element) => {
    const m = latest.rcds.get(tag);
    const v = m ? measurementVerdicts(m, { rcd: r }, project.meta.earthingSystem) : [];
    const bad = v.some((x) => !x.ok);
    return (
      <div
        className={`ms-card${bad ? ' bad' : m ? ' done' : ''}`}
        key={`r-${tag}`}
        data-testid={`ms-rcd-${tag}`}
      >
        <div className="ms-head">
          <strong className="mono">{tag}</strong>
          <span>
            {String(r.props.sensitivityMa ?? 30)} mA · type {String(r.props.rcdType ?? 'A')}
          </span>
          {m && (
            <span className="ms-date">
              {new Date(`${m.date}T12:00:00`).toLocaleDateString(localeTag(locale))}
            </span>
          )}
        </div>
        <fieldset className="ms-fields" disabled={props.readOnly}>
          <div className="ms-field">
            <NumberField
              label={t('measure.tripMs')}
              value={m?.tripMs}
              onCommit={(x) => save('rcd', tag, { tripMs: x })}
              testId={`ms-${tag}-tripMs`}
            />
            {hint(verdictOf(v, 'tripMs'), '')}
          </div>
          <div className="ms-field">
            <NumberField
              label={t('measure.tripMa')}
              value={m?.tripMa}
              onCommit={(x) => save('rcd', tag, { tripMa: x })}
              testId={`ms-${tag}-tripMa`}
            />
            {hint(verdictOf(v, 'tripMa'), '')}
          </div>
          <div className="ms-field">
            <span className="ms-label">{t('measure.testButton')}</span>
            <Chips
              value={m?.testButtonOk === undefined ? undefined : m.testButtonOk ? 'ok' : 'fail'}
              options={[
                { value: 'ok', label: t('measure.trips') },
                { value: 'fail', label: t('measure.noTrip') },
              ]}
              clearable
              onChange={(x) => save('rcd', tag, { testButtonOk: x === undefined ? undefined : x === 'ok' })}
              label={t('measure.testButton')}
              testId={`ms-${tag}-button`}
            />
          </div>
        </fieldset>
        {previous(m)}
        {againButton('rcd', tag, m, bad)}
      </div>
    );
  };

  // The earth electrode's resistance: kept on the diagram's electrode (and the survey board).
  const electrode = Object.values(project.elements).find((e) => e.symbolId === 'iec.earth-electrode');
  const boards = surveyBoards(project);
  const earth = earthNeeds(project, analysis);
  const earthOhm =
    electrode && electrode.props.resistanceOhm !== '' && electrode.props.resistanceOhm !== undefined
      ? Number(electrode.props.resistanceOhm)
      : boards.map((b) => b.earthOhm).find((v) => v !== undefined);
  const showEarth = earth.required || !!electrode || boards.length > 0;
  const earthCard = showEarth && (
    <div
      className={`ms-card${earthOhm === undefined ? '' : earthOhm > earth.maxOhm && earth.required ? ' bad' : ' done'}`}
      data-testid="ms-earth"
    >
      <div className="ms-head">
        <strong className="mono">{electrode?.tag || 'PE'}</strong>
        <span>{t('measure.earth')}</span>
      </div>
      <fieldset className="ms-fields" disabled={props.readOnly}>
        <div className="ms-field">
          <NumberField
            label={t('measure.earthOhm')}
            value={earthOhm}
            onCommit={(x) => {
              const ops = earthResistanceOps(project, x);
              if (ops.length) props.dispatch(t('measure.earth'), ops);
            }}
            testId="ms-earth-ohm"
          />
          {earth.required && earthOhm !== undefined ? (
            <span
              className={`ms-verdict ${earthOhm <= earth.maxOhm ? 'ok' : 'bad'}`}
              data-verdict={earthOhm <= earth.maxOhm ? 'ok' : 'fail'}
            >
              {earthOhm <= earth.maxOhm ? '✓' : '✗'} ≤ {earth.maxOhm} Ω
            </span>
          ) : (
            <span className="ms-verdict">{earth.required ? `≤ ${earth.maxOhm} Ω` : ''}</span>
          )}
        </div>
      </fieldset>
    </div>
  );

  if (!circuits.size && !rcds.size)
    return earthCard ? (
      <div className="ms" data-testid="measurements">
        <div className="ms-list">{earthCard}</div>
        <p className="muted">{t('measure.nothing')}</p>
      </div>
    ) : (
      <p className="muted">{t('measure.nothing')}</p>
    );
  return (
    <div className="ms" data-testid="measurements">
      <label className="sv-field ms-day">
        <span>{t('measure.date')}</span>
        <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
      </label>
      <p className="muted small">{t('measure.intro')}</p>
      {earthCard && <div className="ms-list">{earthCard}</div>}
      {circuits.size > 0 && <h4>{t('measure.circuits')}</h4>}
      <div className="ms-list">{[...circuits].map(([k, c]) => circuitCard(k, c))}</div>
      {rcds.size > 0 && <h4>{t('measure.rcds')}</h4>}
      <div className="ms-list">{[...rcds].map(([k, r]) => rcdCard(k, r))}</div>
    </div>
  );
}

export function MeasurementsDialog(props: {
  project: Project;
  analysis: Analysis;
  t: Translate;
  locale: Locale;
  readOnly: boolean;
  dispatch(label: string, ops: Op[]): void;
  exportCsv(): void;
  onClose(): void;
}) {
  const { t } = props;
  const p = measurementProgress(props.project, props.analysis);
  return (
    <Dialog
      title={t('measure.title')}
      wide
      onClose={props.onClose}
      actions={
        <>
          <span className="muted small grow" data-testid="measure-progress">
            {t('measure.progress', { m: p.measured, c: p.circuits, r: p.tested, n: p.rcds, f: p.failed })}
          </span>
          <button onClick={props.exportCsv} disabled={!Object.keys(props.project.measurements).length}>
            {t('measure.csv')}
          </button>
          <button className="primary" onClick={props.onClose}>
            {t('dialog.close')}
          </button>
        </>
      }
    >
      <MeasurementsEditor {...props} />
    </Dialog>
  );
}
